import React, { useState } from "react";
import { HeaderAction, HeaderState } from "../../types";
import { importHeaderFromCode } from "../../utils/headerImporter";
import Button from "../ui/Button";

interface ImportModalProps {
    onClose: () => void;
    dispatch: React.Dispatch<HeaderAction>;
}

const ImportModal: React.FC<ImportModalProps> = ({ onClose, dispatch }) => {
    const [htmlCode, setHtmlCode] = useState("");
    const [cssCode, setCssCode] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [importStatus, setImportStatus] = useState<"idle" | "processing" | "success" | "error">("idle");

    const handleImport = () => {
        if (!htmlCode.trim() || !cssCode.trim()) {
            setError("Please provide both HTML and CSS code");
            return;
        }

        setImportStatus("processing");
        setError(null);

        try {
            const result = importHeaderFromCode(htmlCode, cssCode);

            if (result.success && result.state) {
                // Update the header state with the imported data
                const newState: HeaderState = {
                    ...result.state,
                    history: [],  // Reset history
                    currentHistoryIndex: -1
                };

                // Use SET_HEADER_STATE action (we need to add this to our action types)
                dispatch({
                    type: "SET_HEADER_STATE",
                    payload: newState
                });

                setImportStatus("success");

                // Close modal after success (with a brief delay to show success)
                setTimeout(() => {
                    onClose();
                }, 1500);
            } else {
                setError(result.error || "Failed to import header");
                setImportStatus("error");
            }
        } catch (err) {
            setError(`Error: ${err instanceof Error ? err.message : String(err)}`);
            setImportStatus("error");
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Import Header</h2>
                    <button
                        className="p-1 rounded-full hover:bg-gray-100"
                        onClick={onClose}
                    >
                        ❌
                    </button>
                </div>

                <div className="mb-6">
                    <p className="text-gray-600 mb-4">
                        Paste the HTML and CSS code of a previously exported header to import and edit it.
                    </p>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                            {error}
                        </div>
                    )}

                    {importStatus === "success" && (
                        <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded">
                            Header imported successfully!
                        </div>
                    )}

                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">HTML Code</label>
                        <textarea
                            className="w-full h-40 p-3 border rounded font-mono text-sm"
                            value={htmlCode}
                            onChange={(e) => setHtmlCode(e.target.value)}
                            placeholder="<header class='header-container'>...</header>"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">CSS Code</label>
                        <textarea
                            className="w-full h-40 p-3 border rounded font-mono text-sm"
                            value={cssCode}
                            onChange={(e) => setCssCode(e.target.value)}
                            placeholder="/* Header Styles */ .header-container { ... }"
                        />
                    </div>
                </div>

                <div className="flex justify-end pt-4 border-t">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="mr-2"
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="primary"
                        onClick={handleImport}
                        disabled={importStatus === "processing"}
                    >
                        {importStatus === "processing" ? "Importing..." : "Import Header"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ImportModal;